import { Paper } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';

import Papa from 'papaparse';
import { useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

type FilePreviewProps = {
  originalFileName: string;
  base64Data: string;
};

export const getFileExtension = (fileName: string) =>
  fileName.split('.').pop()?.toLowerCase() || '';

export const allowedExtensions = [
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'svg',
  'txt',
  'json',
  'csv',
  'html'
];

export const FilePreview: React.FC<FilePreviewProps> = ({
  originalFileName,
  base64Data
}) => {
  const fileExtension = useMemo(
    () => getFileExtension(originalFileName),
    [originalFileName]
  );

  const fileUri = useMemo(() => {
    const mimeTypes: Record<string, string> = {
      gif: 'image/gif',
      jpeg: 'image/jpeg',
      jpg: 'image/jpeg',
      pdf: 'application/pdf',
      png: 'image/png',
      svg: 'image/svg+xml'
    };
    const mimeType = mimeTypes[fileExtension] || 'application/octet-stream';
    return `data:${mimeType};base64,${base64Data}`;
  }, [base64Data, fileExtension]);

  const fileData = useMemo(() => atob(base64Data), [base64Data]);

  const jsonFormattedData = useMemo(
    () =>
      fileExtension === 'json'
        ? JSON.stringify(JSON.parse(fileData), null, 2)
        : '',
    [fileData, fileExtension]
  );

  const htmlContent = useMemo(
    () =>
      fileExtension === 'html'
        ? new TextDecoder().decode(
            new Uint8Array(fileData.split('').map(char => char.charCodeAt(0)))
          )
        : null,
    [fileData, fileExtension]
  );

  const { csvColumns, csvRows } = useMemo(() => {
    if (fileExtension !== 'csv') return { csvColumns: [], csvRows: [] };

    const parsed = Papa.parse(fileData, { header: true, skipEmptyLines: true });
    if (parsed.data.length === 0) return { csvColumns: [], csvRows: [] };

    const columns: GridColDef[] = Object.keys(parsed.data[0]).map(header => ({
      field: header.replace(/"/g, '').trim(),
      flex: 1,
      headerName: header.replace(/"/g, '').trim(),
      minWidth: 100
    }));

    return { csvColumns: columns, csvRows: parsed.data };
  }, [fileData, fileExtension]);

  return (
    <div style={{ height: '100%', width: '100%' }}>
      {fileExtension === 'pdf' && (
        <div
          style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Document file={fileUri} loading='Loading PDF...'>
            <Page pageNumber={1} />
          </Document>
        </div>
      )}

      {['png', 'jpg', 'jpeg', 'gif', 'svg'].includes(fileExtension) && (
        <div
          style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <img
            alt='Preview'
            src={fileUri}
            style={{
              maxHeight: '80vh',
              maxWidth: '100%',
              objectFit: 'contain'
            }}
          />
        </div>
      )}

      {fileExtension === 'json' && (
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {jsonFormattedData}
        </pre>
      )}

      {fileExtension === 'txt' && (
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {fileData}
        </pre>
      )}

      {fileExtension === 'csv' &&
        csvRows.length > 0 &&
        csvColumns.length > 0 && (
          <Paper sx={{ height: 500, width: '100%' }}>
            <DataGridPro
              autoPageSize
              columns={csvColumns}
              density='comfortable'
              disableColumnMenu
              pageSizeOptions={[10, 25, 50]}
              rows={csvRows.map((row: any, index) => ({ id: index, ...row }))}
            />
          </Paper>
        )}

      {fileExtension === 'html' && htmlContent && (
        <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
      )}

      {!allowedExtensions.includes(fileExtension) && (
        <p>Unsupported file format: {originalFileName}</p>
      )}
    </div>
  );
};
